import { useEffect, useState } from 'react';

import { Button } from '@ds/core';
import { Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { CANAIS_DA_CAMPANHA, PUBLICOS, STATUS_DA_CAMPANHA } from '@/data/marketingDaClinica';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { Campanha, DadosDaCampanha, PublicoDaCampanha } from '@/data/tipos';

const VAZIA: DadosDaCampanha = {
    nome: '',
    canal: 'whatsapp',
    publicoTipo: 'sem-retorno',
    publicoParam: { dias: 90 },
    status: 'rascunho',
    investimentoCentavos: 0,
};

/** "1.850,00" e "1850" viram 185000. Campo de dinheiro em centavos, como o resto. */
function emCentavos(texto: string): number {
    const limpo = texto.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const n = Number(limpo);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export function CampanhaModal({ campanha, origens, onClose, onSalvo }: {
    campanha: Campanha | 'nova' | null;
    origens: string[];
    onClose: () => void;
    onSalvo: () => void;
}) {
    const aviso = useAviso();
    const [form, setForm] = useState<DadosDaCampanha>(VAZIA);
    const [investimento, setInvestimento] = useState('');
    const formSujo = useAlteracaoPendente({ form, investimento }, campanha);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!campanha) return;
        if (campanha === 'nova') {
            setForm(VAZIA);
            setInvestimento('');
            return;
        }
        setForm({
            nome: campanha.nome,
            canal: campanha.canal,
            publicoTipo: campanha.publicoTipo,
            publicoParam: campanha.publicoParam,
            ...(campanha.origemLead ? { origemLead: campanha.origemLead } : {}),
            status: campanha.status,
            ...(campanha.inicio ? { inicio: campanha.inicio } : {}),
            ...(campanha.fim ? { fim: campanha.fim } : {}),
            investimentoCentavos: campanha.investimentoCentavos,
        });
        setInvestimento(campanha.investimentoCentavos ? (campanha.investimentoCentavos / 100).toFixed(2).replace('.', ',') : '');
    }, [campanha]);

    const salvar = async () => {
        setSalvando(true);
        try {
            await dados().salvarCampanha(campanha === 'nova' || !campanha ? null : campanha.id, {
                ...form,
                investimentoCentavos: emCentavos(investimento),
            });
            aviso({ title: campanha === 'nova' ? 'Campanha criada' : 'Campanha salva', description: form.nome });
            onSalvo();
        } catch (e) {
            aviso({
                title: 'Não foi possível salvar a campanha',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={campanha !== null}
            title={campanha === 'nova' ? 'Nova campanha' : 'Editar campanha'}
            onClose={onClose}
            dirty={formSujo}
            width={560}
            footer={(
                <>
                    <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                    <Button onClick={salvar} disabled={!form.nome.trim() || salvando}>
                        {salvando ? 'Salvando…' : 'Salvar'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(210px,100%),1fr))', gap: 14 }}>
                <FormField label="Nome" htmlFor="camp-nome" required style={{ gridColumn: '1 / -1' }}>
                    <Input id="camp-nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} placeholder="Reativação — 90 dias sem retorno" />
                </FormField>

                <FormField label="Canal" htmlFor="camp-canal">
                    <Select
                        id="camp-canal" value={form.canal}
                        onChange={(v) => setForm({ ...form, canal: v as Campanha['canal'] })}
                        options={(Object.keys(CANAIS_DA_CAMPANHA) as Campanha['canal'][]).map((c) => ({ value: c, label: CANAIS_DA_CAMPANHA[c] }))}
                    />
                </FormField>

                <FormField label="Situação" htmlFor="camp-status">
                    <Select
                        id="camp-status" value={form.status}
                        onChange={(v) => setForm({ ...form, status: v as Campanha['status'] })}
                        options={(Object.keys(STATUS_DA_CAMPANHA) as Campanha['status'][]).map((s) => ({ value: s, label: STATUS_DA_CAMPANHA[s].rotulo }))}
                    />
                </FormField>

                <FormField label="Público" htmlFor="camp-publico" style={{ gridColumn: '1 / -1' }} hint={PUBLICOS[form.publicoTipo].descricao}>
                    <Select
                        id="camp-publico" value={form.publicoTipo}
                        onChange={(v) => setForm({ ...form, publicoTipo: v as PublicoDaCampanha, publicoParam: v === 'sem-retorno' ? { dias: 90 } : {} })}
                        options={(Object.keys(PUBLICOS) as PublicoDaCampanha[]).map((p) => ({ value: p, label: PUBLICOS[p].rotulo }))}
                    />
                </FormField>

                {form.publicoTipo === 'sem-retorno' && (
                    <FormField label="Dias sem retorno" htmlFor="camp-dias">
                        <Input
                            id="camp-dias" type="number" value={String(form.publicoParam?.dias ?? 90)}
                            onChange={(v) => setForm({ ...form, publicoParam: { dias: Number(v) || 0 } })}
                        />
                    </FormField>
                )}

                {form.publicoTipo === 'por-origem' && (
                    <FormField label="Origem do público" htmlFor="camp-origem-publico">
                        <Input
                            id="camp-origem-publico" value={form.publicoParam?.origem ?? ''}
                            onChange={(v) => setForm({ ...form, publicoParam: { origem: v } })}
                            placeholder="Instagram"
                        />
                    </FormField>
                )}

                {/* Sem origem declarada, a campanha não tem como ser medida — e a
                    tela prefere dizer "sem atribuição" a mostrar zero. */}
                <FormField
                    label="Origem que esta campanha alimenta" htmlFor="camp-origem"
                    hint="É por ela que leads e receita são atribuídos. Sem origem, a campanha não é medida."
                    style={{ gridColumn: '1 / -1' }}
                >
                    <Select
                        id="camp-origem" value={form.origemLead ?? ''}
                        onChange={(v) => setForm({ ...form, ...(v ? { origemLead: v } : { origemLead: undefined }) })}
                        placeholder="Sem atribuição"
                        options={origens.map((o) => ({ value: o, label: o }))}
                    />
                </FormField>

                <FormField label="Início" htmlFor="camp-inicio" hint={form.status === 'rascunho' ? undefined : 'Obrigatório fora do rascunho.'}>
                    <Input id="camp-inicio" type="date" value={form.inicio ?? ''} onChange={(v) => setForm({ ...form, inicio: v || undefined })} />
                </FormField>

                <FormField label="Fim" htmlFor="camp-fim">
                    <Input id="camp-fim" type="date" value={form.fim ?? ''} onChange={(v) => setForm({ ...form, fim: v || undefined })} />
                </FormField>

                <FormField
                    label="Investimento" htmlFor="camp-investimento"
                    hint="O valor que a clínica gastou. Sem ele, custo por lead e retorno ficam de fora."
                    style={{ gridColumn: '1 / -1' }}
                >
                    <Input id="camp-investimento" value={investimento} onChange={setInvestimento} placeholder="1.850,00" iconLeft={<span style={{ fontSize: 12 }}>R$</span>} />
                </FormField>
            </div>
        </Modal>
    );
}
